import React, { useState, useEffect } from 'react';
import { documentService } from '../services/document.service';
import { useAuth } from '../context/AuthContext';

interface ModalProps {
  documentId: string;
  onClose: () => void;
}

export default function AddCollaboratorModal({ documentId, onClose }: ModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('VIEWER');
  const [status, setStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null);
  
  const [isPublic, setIsPublic] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [copyStatus, setCopyStatus] = useState('');

  const { token } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await documentService.getShareSettings(token!, documentId);
        setIsPublic(res.isPublic);
      } catch (err) {
        console.error("Failed to load share settings", err);
      } finally {
        setIsLoadingSettings(false);
      }
    };
    fetchSettings();
  }, [documentId, token]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);

    try {
      await documentService.addCollaborator(token!, documentId, email, role);
      setStatus({ type: 'success', msg: 'Collaborator added successfully!' });
      setEmail('');
      setTimeout(() => setStatus(null), 3000);
    } catch (err: any) {
      setStatus({ type: 'error', msg: err.message });
    }
  };

  const handlePublicToggle = async (newIsPublic: boolean) => {
    setIsPublic(newIsPublic);
    try {
      await documentService.updateShareSettings(token!, documentId, newIsPublic, 'VIEWER');
    } catch (err) {
      console.error("Failed to update public share settings", err);
      setIsPublic(!newIsPublic); // Revert on failure
    }
  };

  const copyToClipboard = () => {
    const url = `${window.location.origin}/d/${documentId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/20">
      <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md border border-gray-300">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold text-gray-800">Share Spreadsheet</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Invite Collaborator</label>
            <div className="flex space-x-2">
              <input 
                type="email" 
                placeholder="Jane.Smith@Example.com" 
                required
                className="flex-1 p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none text-sm transition-all"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              />
              <select 
                className="w-28 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none bg-white text-sm font-medium"
                value={role}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRole(e.target.value)}
              >
                <option value="VIEWER">Viewer</option>
                <option value="COMMENTER">Commenter</option>
                <option value="EDITOR">Editor</option>
              </select>
            </div>
          </div>

          {status && (
            <div className={`p-2 text-sm rounded border ${status.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {status.msg}
            </div>
          )}

          <div className="flex justify-end">
            <button type="submit" className="px-5 py-2 bg-red-600 text-white rounded hover:bg-red-700 shadow font-bold text-sm transition-colors">
              Send Invite
            </button>
          </div>
        </form>

        <div className="my-6 border-t border-gray-200"></div>

        <div className="space-y-4">
          <h3 className="text-sm font-bold text-gray-700">General Access</h3>
          
          {isLoadingSettings ? (
            <div className="animate-pulse flex space-x-4">
              <div className="h-12 bg-gray-100 rounded w-full"></div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 border border-gray-200 rounded bg-gray-50">
              <div className="flex flex-col">
                <select 
                  className="font-bold text-gray-800 bg-transparent outline-none cursor-pointer"
                  value={isPublic ? 'PUBLIC' : 'RESTRICTED'}
                  onChange={(e) => handlePublicToggle(e.target.value === 'PUBLIC')}
                >
                  <option value="RESTRICTED">Restricted</option>
                  <option value="PUBLIC">Anyone with the link</option>
                </select>
                <span className="text-xs text-gray-500 mt-1">
                  {isPublic ? 'Anyone on the internet with the link can view' : 'Only invited people can access'}
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mt-6 pt-2">
            <button 
              type="button"
              onClick={copyToClipboard}
              disabled={!isPublic}
              className={`px-4 py-2 rounded font-bold text-sm transition flex items-center space-x-2 
                ${isPublic ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 shadow-sm' : 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
              <span>{copyStatus || 'Copy Link'}</span>
            </button>

            <button type="button" onClick={onClose} className="px-5 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm rounded text-sm font-bold transition-colors">
              Done
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}